import { environment } from "../../../environments/environment";
import { isLibrarySliceFeatureEnabled } from "../../library/config/library-slice.config";
import { MenuItem } from "../../layout/app.menu.items";
import { Analysis, Granularity, Metric } from "../../dashboards/shared/dashboard-config/dashboard-config.model";
import { BOTTOM_UP_PRODUCT_ID } from "./products.constants";

// Menu Item Flag
interface MenuItemIdentity {
  productId?: number,
  analysisId?: Analysis,
  metricId?: Metric,
  granularity?: Granularity
}

const BOTTOM_UP_DISTRICT_METRICS: Metric[] = [
  Metric.CompaniesCount,
  Metric.CompaniesRevenue,
  Metric.GrossValueAdded,
]

// TODO: add GVA once its index Kreise dashboards exist
const BOTTOM_UP_DISTRICT_INDEX_METRICS: Metric[] = [
  Metric.CompaniesCount,
  Metric.CompaniesRevenue,
]

/** Kreise × WZ-Klassen (124) is a bottom-up product for these metrics and analyses only. */
const BOTTOM_UP_DISTRICT_WZ_METRICS: Metric[] = [
  Metric.CompaniesCount,
  Metric.CompaniesRevenue,
  Metric.GrossValueAdded,
]
const BOTTOM_UP_DISTRICT_WZ_ANALYSES: Analysis[] = [Analysis.TimeSeries, Analysis.Ranking, Analysis.Comparison]

/** Metrics without WZ-class datasets (no dashboards by WZ-Klassen at any level). */
const METRICS_WITHOUT_WZ_CLASSES: Metric[] = [Metric.SelfEmployed, Metric.Tourism]

/** Bottom-up 'nach WZ-Klassen' items stay visible but locked where no dashboard exists. */
function isBottomUpWzClassesLocked(analysisId: Analysis, metricId: number, geoLevelId: number): boolean {
  if (geoLevelId == Granularity.Districts) {
    return !BOTTOM_UP_DISTRICT_WZ_ANALYSES.includes(analysisId) || !BOTTOM_UP_DISTRICT_WZ_METRICS.includes(metricId);
  }
  // Employed is not offered by WZ-Klassen in bottom-up.
  return analysisId === Analysis.Gender || metricId === Metric.Employed || METRICS_WITHOUT_WZ_CLASSES.includes(metricId);
}

const DISABLE_WZ_CLASS_STATES_DASHBOARDS = true; // TODO: enable when feature is ready
const DISABLE_WZ_CLASS_DISTRICTS_DASHBOARDS = true; // TODO: enable when feature is ready
const DISABLE_DISTRICT_WZ_CLASSES_DASHBOARDS = true; // TODO: enable when feature is ready

function isDistrictDashboardsDisabledInCore({ metricId }: MenuItemIdentity): boolean {
  return metricId !== undefined && metricId !== Metric.Employed
}


const ANALYSIS_MENU_PROFILE: Record<Analysis, { label: string, icon: string }> = {
  [Analysis.Dossier]: { label: 'Standortdossier', icon: 'fa-solid fa-book-open' },
  [Analysis.TimeSeries]: { label: 'Zeitreihen', icon: 'fa-solid fa-chart-simple' },
  [Analysis.Ranking]: { label: 'Rankings', icon: 'fa-solid fa-medal' },
  [Analysis.Comparison]: { label: 'Vergleichsanalysen', icon: 'fa-solid fa-scale-balanced' },
  [Analysis.Index]: { label: 'Indexanalysen', icon: 'fa-solid fa-chart-line' },
  [Analysis.Forecast]: { label: 'Prognosen', icon: 'fa-solid fa-binoculars' },
  [Analysis.Gender]: { label: 'Geschlechterverteilung', icon: 'fa-solid fa-venus-mars' },
}

interface AnalysisMenuGroupItems extends MenuItem {
  regionItems?: MenuItem[];
  marketItems?: MenuItem[]
};

function _createAnalysisMenu(type: Analysis, analysisItem: AnalysisMenuGroupItems | MenuItem): MenuItem {
  const items = ('regionItems' in analysisItem || 'marketItems' in analysisItem)
    ? [
        ...(analysisItem.regionItems ? [{ label: 'REGION', items: analysisItem.regionItems }] : []),
        ...(analysisItem.marketItems ? [{ label: 'MARKT', items: analysisItem.marketItems }] : []),
      ]
    : analysisItem.items

  return {
    ...ANALYSIS_MENU_PROFILE[type],
    visible: analysisItem.visible,
    items
  };
}


function _getProductDashboardLink(productId: number, dashboardId: string): string[] {
  return [`/dashboards/${productId.toString()}/${dashboardId}`];
}

function _getStandortdossierLink(productId: number, dashboardId: string, subDashboardName: string): string[] {
  return [`/dashboards/${productId.toString()}/${dashboardId}/${subDashboardName}`];
}

function _getLibraryLink(): string[] {
  return ['/library'];
}


function _getSharedAnalysisMenuItems(productId: number, showDistrictDashboardsInCore: boolean = false): MenuItem[] {
  return [
    {
      label: 'Übersicht',
      icon: 'fa-solid fa-table',
      routerLink: _getProductDashboardLink(productId, '0x0x3')
    },

    ...(isLibrarySliceFeatureEnabled() ? [{
      label: 'Bibliothek',
      icon: 'fa-solid fa-bookmark',
      routerLink: _getLibraryLink()
    }] : []),

    // ----------- TAXED -------------

    {
      label: 'Unternehmen', // When renaming this, also rename in setActiveMainRankings() in chart-personal-page.component.ts
      icon: 'fa-solid fa-building',
      items: [
        _createAnalysisMenu(Analysis.Dossier, {
          items: [
            {
              label: 'Übersicht',
              routerLink: _getStandortdossierLink(productId, '1x1x3', 'übersicht')
            },
            {
              label: 'Marktstruktur',
              routerLink: _getStandortdossierLink(productId, '1x1x3', 'marktstruktur')
            },
            {
              label: 'Marktquerschnitt',
              routerLink: _getStandortdossierLink(productId, '1x1x3', 'marktquerschnitt')
            },
            {
              label: 'Branchenstruktur',
              routerLink: _getStandortdossierLink(productId, '1x1x3', 'branchenstruktur')
            },
            // {
            //   label: 'Performance',
            //   routerLink: _getStandortdossierLink(productId, '1x1x3', 'performance')
            // },
            {
              label: 'Entwicklung',
              routerLink: _getStandortdossierLink(productId, '1x1x3', 'entwicklung')
            }
          ]
        }),
        _createAnalysisMenu(Analysis.TimeSeries, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '2x1x3')
            },
            ...(showDistrictDashboardsInCore ? [{
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '2x1x4')
            }] : []),
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '2x1x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '2x1x12')
            }
          ]
        }),
        _createAnalysisMenu(Analysis.Ranking, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '3x1x3')
            },
            ...(showDistrictDashboardsInCore ? [{
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '3x1x4')
            }] : []),
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '3x1x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '3x1x12')
            },
          ]
        }),
        _createAnalysisMenu(Analysis.Comparison, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '4x1x3')
            },
            ...(showDistrictDashboardsInCore ? [{
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '4x1x4')
            }] : []),
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '4x1x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '4x1x12')
            },
            {
              label: 'Branchen',
              routerLink: _getProductDashboardLink(productId, '4x1x10')
            },
          ]
        }),
        _createAnalysisMenu(Analysis.Forecast, {
          items: [
            {
              label: 'Bundesvergleich Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '5x1x3/bundesvergleich-teilmarkte')
            },
            {
              label: 'Bundesvergleich WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '5x1x3/bundesvergleich-wz-klassen')
            },
            {
              label: 'Marktvergleich Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '5x1x11/marktvergleich-teilmarkte')
            },
            {
              label: 'Marktvergleich WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '5x1x12/marktvergleich-wz-klassen')
            },
            {
              label: 'Branchenstruktur',
              routerLink: _getProductDashboardLink(productId, '5x1x11')
            },
            {
              label: 'Teilmarktstruktur',
              routerLink: _getProductDashboardLink(productId, '5x1x12')
            },
          ]
        })
      ]
    },


    // ----------- REVENUE -------------

    {
      label: 'Umsätze', // When renaming this, also rename in setActiveMainRankings() in chart-personal-page.component.ts
      icon: 'fa-solid fa-coins',
      items: [
        _createAnalysisMenu(Analysis.Dossier, {
          items: [
            {
              label: 'Übersicht',
              routerLink: _getStandortdossierLink(productId, '1x2x3', 'übersicht')
            },
            {
              label: 'Marktstruktur',
              routerLink: _getStandortdossierLink(productId, '1x2x3', 'marktstruktur')
            },
            {
              label: 'Marktquerschnitt',
              routerLink: _getStandortdossierLink(productId, '1x2x3', 'marktquerschnitt')
            },
            {
              label: 'Branchenstruktur',
              routerLink: _getStandortdossierLink(productId, '1x2x3', 'branchenstruktur')
            },
            // {
            //   label: 'Performance',
            //   routerLink: _getStandortdossierLink(productId, '1x2x3', 'performance')
            // },
            {
              label: 'Entwicklung',
              routerLink: _getStandortdossierLink(productId, '1x2x3', 'entwicklung')
            }
          ]
        }),
        _createAnalysisMenu(Analysis.TimeSeries, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '2x2x3')
            },
            ...(showDistrictDashboardsInCore ? [{
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '2x2x4')
            }] : []),
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '2x2x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '2x2x12')
            }
          ]
        }),
        _createAnalysisMenu(Analysis.Ranking, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '3x2x3')
            },
            ...(showDistrictDashboardsInCore ? [{
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '3x2x4')
            }] : []),
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '3x2x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '3x2x12')
            },
          ]
        }),
        _createAnalysisMenu(Analysis.Comparison, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '4x2x3')
            },
            ...(showDistrictDashboardsInCore ? [{
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '4x2x4')
            }] : []),
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '4x2x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '4x2x12')
            },
            {
              label: 'Branchen',
              routerLink: _getProductDashboardLink(productId, '4x2x10')
            },
          ]
        }),
        _createAnalysisMenu(Analysis.Index, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '7x2x3')
            },
            ...(showDistrictDashboardsInCore ? [{
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '7x2x4')
            }] : []),
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '7x2x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '7x2x12')
            },
          ]
        }),
        _createAnalysisMenu(Analysis.Forecast, {
          items: [
            {
              label: 'Bundesvergleich Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '5x2x3/bundesvergleich-teilmarkte')
            },
            {
              label: 'Bundesvergleich WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '5x2x3/bundesvergleich-wz-klassen')
            },
            {
              label: 'Marktvergleich Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '5x2x11/marktvergleich-teilmarkte')
            },
            {
              label: 'Marktvergleich WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '5x2x12/marktvergleich-wz-klassen')
            },
            {
              label: 'Branchenstruktur',
              routerLink: _getProductDashboardLink(productId, '5x2x11')
            },
            {
              label: 'Teilmarktstruktur',
              routerLink: _getProductDashboardLink(productId, '5x2x12')
            },
          ]
        }),
      ]
    },

    // ----------- GROSS VALUE -------------

    ...(!environment.production ? [
    {
      label: 'Bruttowertschöpfung', // When renaming this, also rename in setActiveMainRankings() in chart-personal-page.component.ts
      icon: 'fa-solid fa-money-bill',
      items: [
        _createAnalysisMenu(Analysis.Dossier, {
          items: [
            {
              label: 'Übersicht',
              routerLink: _getStandortdossierLink(productId, '1x3x3', 'übersicht')
            },
            {
              label: 'Marktstruktur',
              routerLink: _getStandortdossierLink(productId, '1x3x3', 'marktstruktur')
            },
            {
              label: 'Marktquerschnitt',
              routerLink: _getStandortdossierLink(productId, '1x3x3', 'marktquerschnitt')
            },
            {
              label: 'Branchenstruktur',
              routerLink: _getStandortdossierLink(productId, '1x3x3', 'branchenstruktur')
            },
            {
              label: 'Entwicklung',
              routerLink: _getStandortdossierLink(productId, '1x3x3', 'entwicklung')
            },
          ]
        }),
        _createAnalysisMenu(Analysis.TimeSeries, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '2x3x3')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '2x3x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '2x3x12')
            }
          ]
        }),
        _createAnalysisMenu(Analysis.Ranking, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '3x3x3')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '3x3x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '3x3x12')
            },
          ]
        }),
        _createAnalysisMenu(Analysis.Comparison, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '4x3x3')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '4x3x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '4x3x12')
            },
            {
              label: 'Branchen',
              routerLink: _getProductDashboardLink(productId, '4x3x10')
            },
          ]
        }),
        _createAnalysisMenu(Analysis.Index, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '7x3x3')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '7x3x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '7x3x12')
            },
          ]
        }),
      ]
    }] : []),


     // ----------- ECONOMIC PARTICIPANTS -------------

    {
      label: 'Erwerbstätige', // When renaming this, also rename in setActiveMainRankings() in chart-personal-page.component.ts
      icon: 'fa-solid fa-briefcase',
      items: [
        _createAnalysisMenu(Analysis.Dossier, {
          items: [
            {
              label: 'Übersicht',
              routerLink: _getStandortdossierLink(productId, '1x4x3', 'übersicht')
            },
            {
              label: 'Marktstruktur',
              routerLink: _getStandortdossierLink(productId, '1x4x3', 'marktstruktur')
            },
            {
              label: 'Marktquerschnitt',
              routerLink: _getStandortdossierLink(productId, '1x4x3', 'marktquerschnitt')
            },
            {
              label: 'Branchenstruktur',
              routerLink: _getStandortdossierLink(productId, '1x4x3', 'branchenstruktur')
            },
            // {
            //   label: 'Performance',
            //   routerLink: _getStandortdossierLink(productId, '1x4x3', 'performance')
            // },
            {
              label: 'Entwicklung',
              routerLink: _getStandortdossierLink(productId, '1x4x3', 'entwicklung')
            }
          ]
        }),
        _createAnalysisMenu(Analysis.TimeSeries, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '2x4x3')
            },
            ...(showDistrictDashboardsInCore ? [{
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '2x4x4')
            }] : []),
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '2x4x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '2x4x12')
            }
          ]
        }),
        _createAnalysisMenu(Analysis.Ranking, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '3x4x3')
            },
            ...(showDistrictDashboardsInCore ? [{
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '3x4x4')
            }] : []),
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '3x4x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '3x4x12')
            },
          ]
        }),
        _createAnalysisMenu(Analysis.Comparison, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '4x4x3')
            },
            ...(showDistrictDashboardsInCore ? [{
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '4x4x4')
            }] : []),
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '4x4x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '4x4x12')
            },
            {
              label: 'Branchen',
              routerLink: _getProductDashboardLink(productId, '4x4x10')
            },
          ]
        }),
        _createAnalysisMenu(Analysis.Index, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '7x4x3')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '7x4x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '7x4x12')
            },
          ]
        }),
        ...(!environment.production ? [
        _createAnalysisMenu(Analysis.Forecast, {
          items: [
            {
              label: 'Bundesvergleich Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '5x4x3/bundesvergleich-teilmarkte')
            },
            {
              label: 'Bundesvergleich WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '5x4x3/bundesvergleich-wz-klassen')
            },
            {
              label: 'Marktvergleich Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '5x4x11/marktvergleich-teilmarkte')
            },
            {
              label: 'Marktvergleich WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '5x4x12/marktvergleich-wz-klassen')
            },
            {
              label: 'Branchenstruktur',
              routerLink: _getProductDashboardLink(productId, '5x4x11')
            },
            {
              label: 'Teilmarktstruktur',
              routerLink: _getProductDashboardLink(productId, '5x4x12')
            },
          ]
        })] : [])
      ]
    },
    // ----------- TOURISM -------------
    // {
    //   label: 'Tourism', // When renaming this, also rename in setActiveMainRankings() in chart-personal-page.component.ts
    //   icon: 'fa-solid fa-umbrella-beach',
    //   items: [
    //     _createAnalysisMenu(Analysis.TimeSeries, {
    //       label: 'Zeitreihen',
    //       items: [
    //         {
    //           label: 'Accommodation',
    //           routerLink: _getProductDashboardLink(productId, '2x12x0')
    //         },
    //       ]
    //     })
    //   ]
    // },

    // ----------- EMPLOYED -------------

    {
      label: 'Beschäftigte', // When renaming this, also rename in setActiveMainRankings() in chart-personal-page.component.ts
      icon: 'fa-solid fa-users',
      items: [
        _createAnalysisMenu(Analysis.Dossier, {
          items: [
            {
              label: 'Übersicht',
              routerLink: _getStandortdossierLink(productId, '1x5x3', 'übersicht')
            },
            {
              label: 'Marktstruktur',
              routerLink: _getStandortdossierLink(productId, '1x5x3', 'marktstruktur')
            },
            {
              label: 'Marktquerschnitt',
              routerLink: _getStandortdossierLink(productId, '1x5x3', 'marktquerschnitt')
            },
            {
              label: 'Branchenstruktur',
              routerLink: _getStandortdossierLink(productId, '1x5x3', 'branchenstruktur')
            },
            // {
            //   label: 'Performance',
            //   routerLink: _getStandortdossierLink(productId, '1x5x3', 'performance')
            // },
            {
              label: 'Entwicklung',
              routerLink: _getStandortdossierLink(productId, '1x5x3', 'entwicklung')
            }
          ]
        }),
        _createAnalysisMenu(Analysis.TimeSeries, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '2x5x3')
            },
            {
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '2x5x4')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '2x5x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '2x5x12')
            }
          ]
        }),
        _createAnalysisMenu(Analysis.Ranking, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '3x5x3')
            },
            {
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '3x5x4')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '3x5x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '3x5x12')
            },
          ]
        }),
        _createAnalysisMenu(Analysis.Comparison, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '4x5x3')
            },
            {
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '4x5x4')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '4x5x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '4x5x12')
            },
            {
              label: 'Branchen',
              routerLink: _getProductDashboardLink(productId, '4x5x10')
            },
          ]
        }),
        _createAnalysisMenu(Analysis.Index, {
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '7x5x3')
            },
            {
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '7x5x4')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '7x5x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '7x5x12')
            },
          ]
        }),
        _createAnalysisMenu(Analysis.Forecast, {
          items: [
            {
              label: 'Bundesvergleich Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '5x5x3/bundesvergleich-teilmarkte')
            },
            {
              label: 'Bundesvergleich WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '5x5x3/bundesvergleich-wz-klassen')
            },
            {
              label: 'Marktvergleich Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '5x5x11/marktvergleich-teilmarkte')
            },
            {
              label: 'Marktvergleich WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '5x5x12/marktvergleich-wz-klassen')
            },
            {
              label: 'Branchenstruktur',
              routerLink: _getProductDashboardLink(productId, '5x5x11')
            },
            {
              label: 'Teilmarktstruktur',
              routerLink: _getProductDashboardLink(productId, '5x5x12')
            },
          ]
        }),
        {
          label: 'Geschlechterverteilung',
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '6x5x3')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '6x5x11')
            }
          ]
        }
      ]
    },


    // ----------- SOLO SELF EMPLOYED -------------

    {
      label: 'Solo-Selbstständige', // When renaming this, also rename in setActiveMainRankings() in chart-personal-page.component.ts
      icon: 'fa-solid fa-user',
      visible: productId == 1,
      items: [
        _createAnalysisMenu(Analysis.TimeSeries, {
          label: 'Zeitreihen',
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '2x7x3')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '2x7x11')
            },
          ]
        }),
        _createAnalysisMenu(Analysis.Ranking, {
          label: 'Rankings',
          items: [
            {
              label: 'Bundesländer',
              routerLink: _getProductDashboardLink(productId, '3x7x3')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '3x7x11')
            },
          ]
        }),
      ]
    },
  ];
}

/** Special analyses only exist for these metrics; others must not render Sonderanalysen. */
const SPECIAL_ANALYSIS_METRICS = new Set<Metric>([
  Metric.CompaniesCount,
  Metric.CompaniesRevenue,
  Metric.Employed,
]);

function _getSpecialAnalysisMenuItems(productId: number, metricId?: Metric): MenuItem[] {
  if (metricId && !SPECIAL_ANALYSIS_METRICS.has(metricId)) {
    return [];
  }

  const specialAnalysisById = new Map<number, MenuItem[]>([
    [1, [
      {
        label: 'Gameswirtschaft ⌛',
        icon: 'fa-solid fa-gamepad',
        requiredClaims: ['access_special_analysis_2'],
        hideWhenNoItems: true,
        items: metricId ? _getGameswirtschaftMetricMenuItems(productId, metricId) : _getGameswirtschaftMenuItems(productId)
      },
      {
        label: 'BaWü Regional',
        icon: 'fa-solid fa-location-dot',
        requiredClaims: ['access_special_analysis_1'],
        hideWhenNoItems: true,
        items: metricId ? _getBaWuRegionalMetricMenuItems(productId, metricId) : _getBaWuRegionalMenuItems(productId)
      },
      {
        label: 'Bayern Regional',
        icon: 'fa-solid fa-location-dot',
        requiredClaims: ['access_special_analysis_4'],
        hideWhenNoItems: true,
        items: metricId ? _getBavariaRegionalMetricMenuItems(productId, metricId) : _getBavariaRegionalMenuItems(productId)
      }
    ]],
    [4, [
      {
        label: 'BaWü Regional',
        icon: 'fa-solid fa-location-dot',
        requiredClaims: ['access_special_analysis_5'],
        hideWhenNoItems: true,
        items: metricId ? _getBaWuRegionalMetricMenuItems(productId, metricId) : _getBaWuRegionalMenuItems(productId)
      },
      {
        label: 'Bayern Regional',
        icon: 'fa-solid fa-location-dot',
        requiredClaims: ['access_special_analysis_3'],
        hideWhenNoItems: true,
        items: metricId ? _getBavariaRegionalMetricMenuItems(productId, metricId) : _getBavariaRegionalMenuItems(productId)
      }
    ]],
    [5, [
      {
        label: 'Bayern Regional',
        icon: 'fa-solid fa-location-dot',
        requiredClaims: ['access_special_analysis_6'],
        hideWhenNoItems: true,
        items: metricId ? _getBavariaRegionalMetricMenuItems(productId, metricId) : _getBavariaRegionalMenuItems(productId)
      }
    ]]
  ])

  return specialAnalysisById.get(productId) || [];
}


function _getBaWuRegionalMetricMenuItems(productId: number, metricId: Metric): MenuItem[] {
  return [
    _createAnalysisMenu(Analysis.TimeSeries, {
      items: [
        {
          label: 'Regierungsbezirke',
          routerLink: _getProductDashboardLink(productId, `8x2x${metricId}x6`)
        },
        {
          label: 'Regionalverbände',
          routerLink: _getProductDashboardLink(productId, `8x2x${metricId}x9`)
        },
        {
          label: 'Kreise & kreisfr. Städte',
          routerLink: _getProductDashboardLink(productId, `8x2x${metricId}x4`)
        },
        {
          label: 'Teilmärkte',
          routerLink: _getProductDashboardLink(productId, `8x2x${metricId}x11`)
        },
        {
          label: 'WZ-Klassen',
          routerLink: _getProductDashboardLink(productId, `8x2x${metricId}x12`)
        }
      ],
    }),
    _createAnalysisMenu(Analysis.Ranking, {
      items: [
        {
          label: 'Kreise & kreisfr. Städte',
          routerLink: _getProductDashboardLink(productId, `8x${metricId}x92`)
        },
        {
          label: 'Teilmärkte',
          routerLink: _getProductDashboardLink(productId, `8x${metricId}x11`)
        },
        {
          label: 'WZ-Klassen',
          routerLink: _getProductDashboardLink(productId, `8x${metricId}x12`)
        }
      ],
    }),
    _createAnalysisMenu(Analysis.Comparison, {
      items: [
        {
          label: 'Kreise & kreisfr. Städte',
          routerLink: _getProductDashboardLink(productId, `8x${metricId}x95`)
        }
      ]
    }),
    _createAnalysisMenu(Analysis.Index, {
      items: [
        {
          label: 'Regierungsbezirke',
          routerLink: _getProductDashboardLink(productId, `8x${metricId}x6`)
        },
        {
          label: 'Regionalverbände',
          routerLink: _getProductDashboardLink(productId, `8x${metricId}x9`)
        },
        {
          label: 'Land-/Stadtkreise',
          routerLink: _getProductDashboardLink(productId, `8x${metricId}x8`)
        },
      ]
    })
  ]
}

function _getBavariaRegionalMetricMenuItems(productId: number, metricId: Metric): MenuItem[] {
  return [
      _createAnalysisMenu(Analysis.TimeSeries, {
        items: [
          {
            label: 'Regierungsbezirke',
            routerLink: _getProductDashboardLink(productId, `10x2x${metricId}x6`)
          },
          {
            label: 'Kreise & kreisfr. Städte',
            routerLink: _getProductDashboardLink(productId, `10x2x${metricId}x4`)
          },
          {
            label: 'Teilmärkte',
            routerLink: _getProductDashboardLink(productId, `10x2x${metricId}x11`)
          },
          {
            label: 'WZ-Klassen',
            routerLink: _getProductDashboardLink(productId, `10x2x${metricId}x12`)
          }
        ],
      }),
      _createAnalysisMenu(Analysis.Ranking, {
        items: [
          {
            label: 'Kreise & kreisfr. Städte',
            routerLink: _getProductDashboardLink(productId, `10x${metricId}x92`)
          },
          {
            label: 'Teilmärkte',
            routerLink: _getProductDashboardLink(productId, `10x${metricId}x11`)
          },
          {
            label: 'WZ-Klassen',
            routerLink: _getProductDashboardLink(productId, `10x${metricId}x12`)
          }
        ],
      }),
      _createAnalysisMenu(Analysis.Comparison, {
        items: [
          {
            label: 'Kreise & kreisfr. Städte',
            routerLink: _getProductDashboardLink(productId, `10x${metricId}x95`)
          }
        ]
      }),
      _createAnalysisMenu(Analysis.Index, {
        items: [
          {
            label: 'Regierungsbezirke',
            routerLink: _getProductDashboardLink(productId, `10x${metricId}x9`)
          },
          {
            label: 'Land-/Stadtkreise',
            routerLink: _getProductDashboardLink(productId, `10x${metricId}x8`)
          },
        ]
      })
    ]
}

function _getGameswirtschaftMetricMenuItems(productId: number, metricId: Metric): MenuItem[] {
  return[
    {
      label: 'Mapping',
      visible: metricId == Metric.CompaniesCount,
      routerLink: _getProductDashboardLink(productId, `9x${metricId}x91`)
    },
    {
      label: 'Verteilung',
      visible: metricId == Metric.CompaniesCount,
      routerLink: _getProductDashboardLink(productId, `9x${metricId}x93`)
    },
    {
      label: 'Zeitverlauf',
      routerLink: _getProductDashboardLink(productId, `9x${metricId}x94`)
    },
  ];
}


function _getGameswirtschaftMenuItems(productId: number): MenuItem[] {
  return [
    {
      label: 'Unternehmen/Institutionen',
      items: [
        {
          label: 'Mapping',
          routerLink: _getProductDashboardLink(productId, '9x11x91')
        },
        {
          label: 'Verteilung',
          routerLink: _getProductDashboardLink(productId, '9x11x93')
        },
        {
          label: 'Zeitverlauf',
          routerLink: _getProductDashboardLink(productId, '9x11x94')
        },
      ]
    },
    {
      label: 'Beschäftigte',
      items: [
        {
          label: 'Zeitverlauf',
          routerLink: _getProductDashboardLink(productId, '9x5x94')
        },
      ]
    },
  ]
}

function _getBaWuRegionalMenuItems(productId: number): MenuItem[] {
  return [
    {
      label: 'Unternehmen',
      items: [
        {
          label: 'Zeitreihen',
          items: [
            {
              label: 'Regierungsbezirke',
              routerLink: _getProductDashboardLink(productId, '8x2x1x6')
            },
            {
              label: 'Regionalverbände',
              routerLink: _getProductDashboardLink(productId, '8x2x1x9')
            },
            {
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '8x2x1x4')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '8x2x1x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '8x2x1x12')
            }
          ]
        },
        {
          label: 'Teilmärkte',
          routerLink: _getProductDashboardLink(productId, '8x1x11')
        },
        {
          label: 'WZ-Klassen',
          routerLink: _getProductDashboardLink(productId, '8x1x12')
        },
        {
          label: 'Mapping & Ranking',
          routerLink: _getProductDashboardLink(productId, '8x1x92')
        },
        {
          label: 'Vergleichsanalysen',
          routerLink: _getProductDashboardLink(productId, '8x1x95')
        },
        {
          label: 'Indexanalysen',
          items: [
            {
              label: 'Regierungsbezirke',
              routerLink: _getProductDashboardLink(productId, '8x1x6')
            },
            {
              label: 'Regionalverbände',
              routerLink: _getProductDashboardLink(productId, '8x1x9')
            },
            {
              label: 'Land-/Stadtkreise',
              routerLink: _getProductDashboardLink(productId, '8x1x8')
            },
          ]
        },
      ]
    },
    {
      label: 'Umsätze',
      items: [
        {
          label: 'Zeitreihen',
          items: [
            {
              label: 'Regierungsbezirke',
              routerLink: _getProductDashboardLink(productId, '8x2x2x6')
            },
            {
              label: 'Regionalverbände',
              routerLink: _getProductDashboardLink(productId, '8x2x2x9')
            },
            {
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '8x2x2x4')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '8x2x2x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '8x2x2x12')
            }
          ]
        },
        {
          label: 'Teilmärkte',
          routerLink: _getProductDashboardLink(productId, '8x2x11')
        },
        {
          label: 'WZ-Klassen',
          routerLink: _getProductDashboardLink(productId, '8x2x12')
        },
        {
          label: 'Mapping & Ranking',
          routerLink: _getProductDashboardLink(productId, '8x2x92')
        },
        {
          label: 'Vergleichsanalysen',
          routerLink: _getProductDashboardLink(productId, '8x2x95')
        },
        {
          label: 'Indexanalysen',
          items: [
            {
              label: 'Regierungsbezirke',
              routerLink: _getProductDashboardLink(productId, '8x2x6')
            },
            {
              label: 'Regionalverbände',
              routerLink: _getProductDashboardLink(productId, '8x2x9')
            },
            {
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '8x2x8')
            },
          ]
        },
      ]
    },
    {
      label: 'Beschäftigte',
      items: [
        {
          label: 'Zeitreihen',
          items: [
            {
              label: 'Regierungsbezirke',
              routerLink: _getProductDashboardLink(productId, '8x2x5x6')
            },
            {
              label: 'Regionalverbände',
              routerLink: _getProductDashboardLink(productId, '8x2x5x9')
            },
            {
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '8x2x5x4')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '8x2x5x11')
            },
          ]
        },
        {
          label: 'Teilmärkte',
          routerLink: _getProductDashboardLink(productId, '8x5x11')
        },
        {
          label: 'Mapping & Ranking',
          routerLink: _getProductDashboardLink(productId, '8x5x92')
        },
        {
          label: 'Vergleichsanalysen',
          routerLink: _getProductDashboardLink(productId, '8x5x95')
        },
        {
          label: 'Indexanalysen',
          items: [
            {
              label: 'Regierungsbezirke',
              routerLink: _getProductDashboardLink(productId, '8x5x6')
            },
            {
              label: 'Regionalverbände',
              routerLink: _getProductDashboardLink(productId, '8x5x9')
            },
            {
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '8x5x8')
            },
          ]
        },
      ]
    },
  ]
}

function _getBavariaRegionalMenuItems(productId: number): MenuItem[] {
  return [
    {
      label: 'Unternehmen',
      items: [
        {
          label: 'Zeitreihen',
          items: [
            {
              label: 'Regierungsbezirke',
              routerLink: _getProductDashboardLink(productId, '10x2x1x6')
            },
            {
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '10x2x1x4')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '10x2x1x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '10x2x1x12')
            }
          ]
        },
        {
          label: 'Teilmärkte',
          routerLink: _getProductDashboardLink(productId, '10x1x11')
        },
        {
          label: 'WZ-Klassen',
          routerLink: _getProductDashboardLink(productId, '10x1x12')
        },
        {
          label: 'Mapping & Ranking',
          routerLink: _getProductDashboardLink(productId, '10x1x92')
        },
        {
          label: 'Vergleichsanalysen',
          routerLink: _getProductDashboardLink(productId, '10x1x95')
        },
        {
          label: 'Indexanalysen',
          items: [
            {
              label: 'Regierungsbezirke',
              routerLink: _getProductDashboardLink(productId, '10x1x9')
            },
            {
              label: 'Land-/Stadtkreise',
              routerLink: _getProductDashboardLink(productId, '10x1x8')
            },
          ]
        },
      ]
    },
    {
      label: 'Umsätze',
      items: [
        {
          label: 'Zeitreihen',
          items: [
            {
              label: 'Regierungsbezirke',
              routerLink: _getProductDashboardLink(productId, '10x2x2x6')
            },
            {
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '10x2x2x4')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '10x2x2x11')
            },
            {
              label: 'WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, '10x2x2x12')
            }
          ]
        },
        {
          label: 'Teilmärkte',
          routerLink: _getProductDashboardLink(productId, '10x2x11')
        },
        {
          label: 'WZ-Klassen',
          routerLink: _getProductDashboardLink(productId, '10x2x12')
        },
        {
          label: 'Mapping & Ranking',
          routerLink: _getProductDashboardLink(productId, '10x2x92')
        },
        {
          label: 'Vergleichsanalysen',
          routerLink: _getProductDashboardLink(productId, '10x2x95')
        },
        {
          label: 'Indexanalysen',
          items: [
            {
              label: 'Regierungsbezirke',
              routerLink: _getProductDashboardLink(productId, '10x2x9')
            },
            {
              label: 'Land-/Stadtkreise',
              routerLink: _getProductDashboardLink(productId, '10x2x8')
            },
          ]
        },
      ]
    },
    {
      label: 'Beschäftigte',
      items: [
        {
          label: 'Zeitreihen',
          items: [
            {
              label: 'Regierungsbezirke',
              routerLink: _getProductDashboardLink(productId, '10x2x5x6')
            },
            {
              label: 'Kreise & kreisfr. Städte',
              routerLink: _getProductDashboardLink(productId, '10x2x5x4')
            },
            {
              label: 'Teilmärkte',
              routerLink: _getProductDashboardLink(productId, '10x2x5x11')
            },
          ]
        },
        {
          label: 'Teilmärkte',
          routerLink: _getProductDashboardLink(productId, '10x5x11')
        },
        {
          label: 'Mapping & Ranking',
          routerLink: _getProductDashboardLink(productId, '10x5x92')
        },
        {
          label: 'Vergleichsanalysen',
          routerLink: _getProductDashboardLink(productId, '10x5x95')
        },
        {
          label: 'Indexanalysen',
          items: [
            {
              label: 'Regierungsbezirke',
              routerLink: _getProductDashboardLink(productId, '10x5x9')
            },
            {
              label: 'Land-/Stadtkreise',
              routerLink: _getProductDashboardLink(productId, '10x5x8')
            },
          ]
        },
      ]
    },
  ]
}

function _getProductMenuItems(productId: number, analysisMenuItems: MenuItem[], specialAnalysisMenuItems: MenuItem[]): MenuItem[] {
  return [
    {
      label: 'Analysen',
      icon: 'pi pi-th-large',
      items: analysisMenuItems
    },
    {
      label: 'Sonderanalysen',
      icon: 'pi pi-th-large',
      hideWhenNoItems: true,
      items: specialAnalysisMenuItems
    },
    {
      label: 'Methodik',
      items: [
        {
          label: 'Datenquellen',
          icon: 'pi pi-info-circle',
          routerLink: _getProductDashboardLink(productId, 'methodology-data-source')
        },
        {
          label: 'Datenverarbeitung',
          icon: 'pi pi-chart-bar',
          routerLink: _getProductDashboardLink(productId, 'methodology-data-processing')
        },
        {
          label: 'Erfassungssystematiken',
          icon: 'pi pi-box',
          routerLink: _getProductDashboardLink(productId, 'methodology-table')
        },
        {
          label: 'Methodik-Vergleich',
          icon: 'pi pi-th-large',
          routerLink: _getProductDashboardLink(productId, 'methodology-table-comparison')
        },
      ]
    }
  ] as MenuItem[]
}

interface BottomUpMenuItemsParameter {
  productId: number;
  metricId: number;
  dimensionIds: {
    geoLevelId: number;
    submarketDimensionId: number;
    wzClassDimensionId: number;
    geoLevelTitle: string;
  };
}

function _buildBottomUpGeoLevelMenuItems({ productId, metricId, dimensionIds: { geoLevelId, submarketDimensionId, wzClassDimensionId, geoLevelTitle } }: BottomUpMenuItemsParameter): MenuItem[] {
  return [
    _createAnalysisMenu(Analysis.TimeSeries, {
      items: [
        {
          label: `nach ${geoLevelTitle}`,
          routerLink: _getProductDashboardLink(productId, `2x${metricId}x${geoLevelId}`),
          disabled: geoLevelId == 4 && !BOTTOM_UP_DISTRICT_METRICS.includes(metricId)
        },
        {
          label: 'nach Teilmärkten',
          routerLink: _getProductDashboardLink(productId, `2x${metricId}x${submarketDimensionId}`),
          disabled: geoLevelId == 4 && !BOTTOM_UP_DISTRICT_METRICS.includes(metricId)
        },
        {
          label: 'nach WZ-Klassen',
          routerLink: _getProductDashboardLink(productId, `2x${metricId}x${wzClassDimensionId}`),
          disabled: isBottomUpWzClassesLocked(Analysis.TimeSeries, metricId, geoLevelId)
        },
      ]
    }),
    _createAnalysisMenu(Analysis.Ranking, {
      items: [
        {
          label: `nach ${geoLevelTitle}`,
          routerLink: _getProductDashboardLink(productId, `3x${metricId}x${geoLevelId}`),
          disabled: geoLevelId == 4 && !BOTTOM_UP_DISTRICT_METRICS.includes(metricId)
        },
        {
          label: 'nach Teilmärkten',
          routerLink: _getProductDashboardLink(productId, `3x${metricId}x${submarketDimensionId}`),
          disabled: geoLevelId == 4 && !BOTTOM_UP_DISTRICT_METRICS.includes(metricId)
        },
        {
          label: 'nach WZ-Klassen',
          routerLink: _getProductDashboardLink(productId, `3x${metricId}x${wzClassDimensionId}`),
          disabled: isBottomUpWzClassesLocked(Analysis.Ranking, metricId, geoLevelId)
        },
      ]
    }),
    _createAnalysisMenu(Analysis.Comparison, {
      items: [
        {
          label: `nach ${geoLevelTitle}`,
          routerLink: _getProductDashboardLink(productId, `4x${metricId}x${geoLevelId}`),
          disabled: geoLevelId == 4 && !BOTTOM_UP_DISTRICT_METRICS.includes(metricId)
        },
        {
          label: 'nach Teilmärkten',
          routerLink: _getProductDashboardLink(productId, `4x${metricId}x${submarketDimensionId}`),
          disabled: geoLevelId == 4 && !BOTTOM_UP_DISTRICT_METRICS.includes(metricId)
        },
        {
          label: 'nach WZ-Klassen',
          routerLink: _getProductDashboardLink(productId, `4x${metricId}x${wzClassDimensionId}`),
          disabled: isBottomUpWzClassesLocked(Analysis.Comparison, metricId, geoLevelId)
        },
      ]
    }),
    _createAnalysisMenu(Analysis.Index, {
      visible: metricId !== Metric.CompaniesCount,
      items: [
        {
          label: `nach ${geoLevelTitle}`,
          routerLink: _getProductDashboardLink(productId, `7x${metricId}x${geoLevelId}`),
          disabled: geoLevelId == 4 && !BOTTOM_UP_DISTRICT_INDEX_METRICS.includes(metricId)
        },
        {
          label: 'nach Teilmärkten',
          routerLink: _getProductDashboardLink(productId, `7x${metricId}x${submarketDimensionId}`),
          disabled: geoLevelId == 4 && !BOTTOM_UP_DISTRICT_INDEX_METRICS.includes(metricId)
        },
        {
          label: 'nach WZ-Klassen',
          routerLink: _getProductDashboardLink(productId, `7x${metricId}x${wzClassDimensionId}`),
          disabled: isBottomUpWzClassesLocked(Analysis.Index, metricId, geoLevelId)
        },
      ]
    }),
    _createAnalysisMenu(Analysis.Gender, {
      visible: metricId === Metric.Employed && geoLevelId == Granularity.States,
      items: [
        {
          label: `nach ${geoLevelTitle}`,
          routerLink: _getProductDashboardLink(productId, `6x${metricId}x${geoLevelId}`),
          disabled: geoLevelId == 4 && !BOTTOM_UP_DISTRICT_METRICS.includes(metricId)
        },
        {
          label: 'nach Teilmärkten',
          routerLink: _getProductDashboardLink(productId, `6x${metricId}x${submarketDimensionId}`),
          disabled: geoLevelId == 4 && !BOTTOM_UP_DISTRICT_METRICS.includes(metricId)
        },
        {
          label: 'nach WZ-Klassen',
          routerLink: _getProductDashboardLink(productId, `6x${metricId}x${wzClassDimensionId}`),
          disabled: isBottomUpWzClassesLocked(Analysis.Gender, metricId, geoLevelId)
        },
      ]
    })
  ]
}

function _getBottomUpMenuItems(productId: number, metricId: number): MenuItem[] {
  return [

    (!environment.production ? {
      label: 'Übersicht',
      icon: 'fa-solid fa-table',
      routerLink: _getProductDashboardLink(productId, 'bu-0x0x3')
    } : {}),

    ...(isLibrarySliceFeatureEnabled() ? [{
      label: 'Bibliothek',
      icon: 'fa-solid fa-bookmark',
      routerLink: _getLibraryLink()
    }] : []),

    {
      label: 'Landkreis',
      icon: 'fa-solid fa-location-dot',
      items: _buildBottomUpGeoLevelMenuItems({ productId, metricId, dimensionIds: {
        geoLevelId: Granularity.Districts,
        submarketDimensionId: Granularity.DistrictSubMarkets,
        wzClassDimensionId: Granularity.DistrictWzClasses,
        geoLevelTitle: 'Landkreisen'
      }})
    },

    {
      label: 'Bundesland',
      icon: 'fa-solid fa-layer-group',
      items: _buildBottomUpGeoLevelMenuItems({ productId, metricId, dimensionIds: {
        geoLevelId: Granularity.States,
        submarketDimensionId: Granularity.SubMarkets,
        wzClassDimensionId: Granularity.WzClasses,
        geoLevelTitle: 'Bundesländern'
      }})
    },
  ];
}


function _buildTopDownMenuItems({ productId, metricId }: { productId: number, metricId: number }): MenuItem[] {
  return [
    _createAnalysisMenu(Analysis.Dossier, {
      visible: metricId !== Metric.SelfEmployed,
      items: [
        {
          label: 'Übersicht',
          routerLink: _getStandortdossierLink(productId, `1x${metricId}x3`, 'übersicht')
        },
        {
          label: 'Marktstruktur',
          routerLink: _getStandortdossierLink(productId, `1x${metricId}x3`, 'marktstruktur')
        },
        {
          label: 'Marktquerschnitt',
          routerLink: _getStandortdossierLink(productId, `1x${metricId}x3`, 'marktquerschnitt')
        },
        {
          label: 'Branchenstruktur',
          routerLink: _getStandortdossierLink(productId, `1x${metricId}x3`, 'branchenstruktur')
        },
        {
          label: 'Entwicklung',
          routerLink: _getStandortdossierLink(productId, `1x${metricId}x3`, 'entwicklung')
        }
      ],
    }),
    _createAnalysisMenu(Analysis.TimeSeries, {
      regionItems: [
        {
          label: 'Bundesländer',
          items: [
            {
              label: 'nach Teilmärkten',
              routerLink: _getProductDashboardLink(productId, `2x${metricId}x11`)
            },
            {
              label: 'nach WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, `2x${metricId}x12`),
              disabled: metricId === Metric.SelfEmployed
            },
          ]
        },
        {
          label: 'Kreise & kreisfr. Städte',
          items: [
            {
              label: 'nach Teilmärkten',
              routerLink: _getProductDashboardLink(productId, `2x${metricId}x114`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId }) || metricId === Metric.SelfEmployed
            },
            {
              label: 'nach WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, `2x${metricId}x124`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId }) || DISABLE_DISTRICT_WZ_CLASSES_DASHBOARDS || metricId === Metric.SelfEmployed
            },
          ],
        },
      ],
      marketItems: [
        {
          label: 'Teilmärkte',
          items: [
            {
              label: 'nach Bundesländern',
              routerLink: _getProductDashboardLink(productId, `2x${metricId}x3`)
            },
            {
              label: 'nach Landkreisen',
              routerLink: _getProductDashboardLink(productId, `2x${metricId}x4`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId }) || metricId === Metric.SelfEmployed,
            },
          ],
        },
        {
          label: 'WZ-Klassen',
          items: [
            {
              label: 'nach Bundesländern',
              routerLink: _getProductDashboardLink(productId, `2x${metricId}x312`) ,
              disabled: metricId === Metric.SelfEmployed,
            },
            {
              label: 'nach Landkreisen',
              routerLink: _getProductDashboardLink(productId, `2x${metricId}x412`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId }) || DISABLE_WZ_CLASS_DISTRICTS_DASHBOARDS || metricId === Metric.SelfEmployed,
            },
          ],
        },
      ],
    }),
    _createAnalysisMenu(Analysis.Ranking, {
      regionItems: [
        {
          label: 'Bundesländer',
          items: [
            {
              label: 'nach Teilmärkten',
              routerLink: _getProductDashboardLink(productId, `3x${metricId}x11`)
            },
            {
              label: 'nach WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, `3x${metricId}x12`),
              disabled: metricId === Metric.SelfEmployed
            },
          ]
        },
        {
          label: 'Kreise & kreisfr. Städte',
          items: [
            {
              label: 'nach Teilmärkten',
              routerLink: _getProductDashboardLink(productId, `3x${metricId}x114`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId }) || metricId === Metric.SelfEmployed
            },
            {
              label: 'nach WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, `3x${metricId}x124`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId }) || DISABLE_DISTRICT_WZ_CLASSES_DASHBOARDS || metricId === Metric.SelfEmployed
            },
          ],
        },
      ],
      marketItems: [
        {
          label: 'Teilmärkte',
          items: [
            {
              label: 'nach Bundesländern',
              routerLink: _getProductDashboardLink(productId, `3x${metricId}x3`)
            },
            {
              label: 'nach Landkreisen',
              routerLink: _getProductDashboardLink(productId, `3x${metricId}x4`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId }) || metricId === Metric.SelfEmployed
            },
          ],
        },
        {
          label: 'WZ-Klassen',
          items: [
            {
              label: 'nach Bundesländern',
              routerLink: _getProductDashboardLink(productId, `3x${metricId}x312`),
              disabled: metricId === Metric.SelfEmployed
            },
            {
              label: 'nach Landkreisen',
              routerLink: _getProductDashboardLink(productId, `3x${metricId}x412`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId }) || DISABLE_WZ_CLASS_DISTRICTS_DASHBOARDS || metricId === Metric.SelfEmployed,
            },
          ],
        },
      ],
    }),
    _createAnalysisMenu(Analysis.Comparison, {
      visible: metricId !== Metric.SelfEmployed,
      regionItems: [
        {
          label: 'Bundesländer',
          items: [
            {
              label: 'nach Teilmärkten',
              routerLink: _getProductDashboardLink(productId, `4x${metricId}x11`)
            },
            {
              label: 'nach WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, `4x${metricId}x12`)
            },
          ]
        },
        {
          label: 'Kreise & kreisfr. Städte',
          items: [
            {
              label: 'nach Teilmärkten',
              routerLink: _getProductDashboardLink(productId, `4x${metricId}x114`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId })
            },
            {
              label: 'nach WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, `4x${metricId}x124`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId }) || DISABLE_DISTRICT_WZ_CLASSES_DASHBOARDS
            },
          ],
        },
      ],
      marketItems: [
        {
          label: 'Teilmärkte',
          items: [
            {
              label: 'nach Bundesländern',
              routerLink: _getProductDashboardLink(productId, `4x${metricId}x3`)
            },
            {
              label: 'nach Landkreisen',
              routerLink: _getProductDashboardLink(productId, `4x${metricId}x4`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId })
            },
          ],
        },
        {
          label: 'WZ-Klassen',
          items: [
            {
              label: 'nach Bundesländern',
              routerLink: _getProductDashboardLink(productId, `4x${metricId}x312`),
            },
            {
              label: 'nach Landkreisen',
              routerLink: _getProductDashboardLink(productId, `4x${metricId}x412`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId }) || DISABLE_WZ_CLASS_DISTRICTS_DASHBOARDS,
            },
          ],
        },
      ],
    }),
    _createAnalysisMenu(Analysis.Index, {
      visible: metricId !== Metric.CompaniesCount && metricId !== Metric.SelfEmployed,
      regionItems: [
        {
          label: 'Bundesländer',
          items: [
            {
              label: 'nach Teilmärkten',
              routerLink: _getProductDashboardLink(productId, `7x${metricId}x11`)
            },
            {
              label: 'nach WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, `7x${metricId}x12`)
            },
          ]
        },
        {
          label: 'Kreise & kreisfr. Städte',
          items: [
            {
              label: 'nach Teilmärkten',
              routerLink: _getProductDashboardLink(productId, `7x${metricId}x114`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId })
            },
            {
              label: 'nach WZ-Klassen',
              routerLink: _getProductDashboardLink(productId, `7x${metricId}x124`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId }) || DISABLE_DISTRICT_WZ_CLASSES_DASHBOARDS
            },
          ],
        },
      ],
      marketItems: [
        {
          label: 'Teilmärkte',
          items: [
            {
              label: 'nach Bundesländern',
              routerLink: _getProductDashboardLink(productId, `7x${metricId}x3`)
            },
            {
              label: 'nach Landkreisen',
              routerLink: _getProductDashboardLink(productId, `7x${metricId}x4`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId })
            },
          ],
        },
        {
          label: 'WZ-Klassen',
          items: [
            {
              label: 'nach Bundesländern',
              routerLink: _getProductDashboardLink(productId, `7x${metricId}x312`),
            },
            {
              label: 'nach Landkreisen',
              routerLink: _getProductDashboardLink(productId, `7x${metricId}x412`),
              disabled: isDistrictDashboardsDisabledInCore({ metricId }) || DISABLE_WZ_CLASS_DISTRICTS_DASHBOARDS,
            },
          ],
        },
      ],
    }),
    _createAnalysisMenu(Analysis.Forecast, {
      visible: metricId !== Metric.GrossValueAdded && metricId !== Metric.SelfEmployed,
      regionItems: [
        {
          label: 'Bundesvergleich Teilmärkte',
          routerLink: _getProductDashboardLink(productId, `5x${metricId}x3/bundesvergleich-teilmarkte`)
        },
        {
          label: 'Bundesvergleich WZ-Klassen',
          routerLink: _getProductDashboardLink(productId, `5x${metricId}x3/bundesvergleich-wz-klassen`)
        },
      ],
      marketItems: [
        {
          label: 'Marktvergleich Teilmärkte',
          routerLink: _getProductDashboardLink(productId, `5x${metricId}x11/marktvergleich-teilmarkte`)
        },
        {
          label: 'Marktvergleich WZ-Klassen',
          routerLink: _getProductDashboardLink(productId, `5x${metricId}x12/marktvergleich-wz-klassen`)
        },
        {
          label: 'Branchenstruktur',
          routerLink: _getProductDashboardLink(productId, `5x${metricId}x11`)
        },
        {
          label: 'Teilmarktstruktur',
          routerLink: _getProductDashboardLink(productId, `5x${metricId}x12`)
        },
      ],
    }),
    _createAnalysisMenu(Analysis.Gender, {
      visible: metricId === Metric.Employed,
      regionItems: [
        {
          label: 'Bundesländer',
          routerLink: _getProductDashboardLink(productId, `6x${metricId}x3`)
        },
      ],
      marketItems: [
        {
          label: 'Teilmärkte',
          routerLink: _getProductDashboardLink(productId, `6x${metricId}x11`)
        },
      ],
    })
  ]
}


function _getTopDownMenuItems(productId: number, metricId: number): MenuItem[] {
  return [
    {
      label: 'Übersicht',
      icon: 'fa-solid fa-table',
      routerLink: _getProductDashboardLink(productId, '0x0x3')
    },

    ...(isLibrarySliceFeatureEnabled() ? [{
      label: 'Bibliothek',
      icon: 'fa-solid fa-bookmark',
      routerLink: _getLibraryLink()
    }] : []),

    ..._buildTopDownMenuItems({ productId, metricId })
  ]
}

/**
 * Both top-down and bottom-up menus covers a single metric only and is
 * therefore rebuilt whenever the metric selected in the sidebar changes.
 * The only difference is that bottom-up menu only applicable to certain product
 */
const isNavModeFeatureEnabled = !environment.legacyNavigation

export function getProductMenuItems(productId: number, metricId: Metric): MenuItem[] {
  if (!isNavModeFeatureEnabled) {
    return _getProductMenuItems(
      productId,
      _getSharedAnalysisMenuItems(productId),
      _getSpecialAnalysisMenuItems(productId)
    )
  }

  if (productId === BOTTOM_UP_PRODUCT_ID) {
    return _getProductMenuItems(
      BOTTOM_UP_PRODUCT_ID,
      _getBottomUpMenuItems(BOTTOM_UP_PRODUCT_ID, metricId),
      _getSpecialAnalysisMenuItems(BOTTOM_UP_PRODUCT_ID, metricId)
    );
  }

  return _getProductMenuItems(
    productId,
    _getTopDownMenuItems(productId, metricId),
    _getSpecialAnalysisMenuItems(productId, metricId)
  );
}
